import Button from '@ui/Button'
import Sheet from '@ui/Sheet'
import type { MultiFactorResolver, User } from 'firebase/auth'
import { type FormEvent, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { useRemoveMyPasswordMutation } from '@/__generated__/hooks'
import {
  IdpError,
  type IdpErrorCode,
  reauthWithGoogle,
  resolveTotp,
  signOutIdp,
} from '@/auth/idp'

import OneTimeCodeField from './OneTimeCodeField'
import { idpErrorKey } from './ReauthForm'

// Only opened when Google is connected, so removing the password can never
// lock anyone out. The confirmation comes first so the destructive tap itself
// opens Google's popup (Safari blocks popups that don't follow a tap), and the
// re-check is always Google: people remove passwords they've forgotten.

type Props = {
  open: boolean
  email: string
  /** 2FA stays on after removal and then applies to Google sign-in. */
  twoFactor: boolean
  /** A never-confirmed password: nothing to lose, so the copy is simpler. */
  unverified: boolean
  onClose: () => void
  /** Called once the password is gone, before the sheet closes. */
  onChanged: () => void
}

const RemovePasswordSheet = ({
  open,
  email,
  twoFactor,
  unverified,
  onClose,
  onChanged,
}: Props) => {
  const { t } = useTranslation()
  const [resolver, setResolver] = useState<MultiFactorResolver | null>(null)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<IdpErrorCode | null>(null)
  const [removeMyPassword] = useRemoveMyPasswordMutation()

  useEffect(() => {
    if (open) return
    setResolver(null)
    setCode('')
    setError(null)
    signOutIdp()
  }, [open])

  // The API removes it: it checks the fresh Google re-check and puts back the
  // account email that unlinking clears (without it, Google sign-in breaks).
  const finish = async (user: User) => {
    const idToken = await user.getIdToken()
    await signOutIdp()
    try {
      await removeMyPassword({ variables: { idToken } })
    } catch (err) {
      const message = (err as { message?: string })?.message ?? ''
      throw new IdpError(
        message.includes('user-mismatch') ? 'user-mismatch' : 'unknown'
      )
    }
    onChanged()
    onClose()
  }

  const run = async (work: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    try {
      await work()
    } catch (err) {
      const errCode = err instanceof IdpError ? err.code : 'unknown'
      setError(errCode === 'cancelled' ? null : errCode)
    } finally {
      setBusy(false)
    }
  }

  const onConfirm = () =>
    run(async () => {
      const result = await reauthWithGoogle(email)
      if (result.kind === 'totp-required') {
        setResolver(result.resolver)
        return
      }
      await finish(result.user)
    })

  const onCode = (e: FormEvent) => {
    e.preventDefault()
    if (!resolver) return
    run(async () => {
      await finish(await resolveTotp(resolver, code.trim()))
    })
  }

  const errorLine = error && (
    <p role="alert" className="text-sm text-flag">
      {t(idpErrorKey(error), { email })}
    </p>
  )

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={t('profile.remove-password.title')}
    >
      {resolver ? (
        <form key="code" onSubmit={onCode} className="flex flex-col gap-4">
          <p className="text-sm text-ink-700">
            {t('auth.password.totp-intro')}
          </p>
          <OneTimeCodeField value={code} onChange={setCode} />
          {errorLine}
          <Button type="submit" fullWidth disabled={busy || code.length < 6}>
            {busy
              ? t('profile.remove-password.busy')
              : t('auth.password.verify')}
          </Button>
        </form>
      ) : (
        <div className="flex flex-col gap-4">
          {unverified ? (
            <p className="text-sm text-ink-700">
              {t('profile.remove-password.body-unverified')}
            </p>
          ) : (
            <>
              <p className="text-sm text-ink-700">
                <Trans
                  i18nKey="profile.remove-password.body"
                  values={{ email }}
                  components={{ strong: <strong /> }}
                />
              </p>
              {twoFactor && (
                <p className="text-sm text-ink-700">
                  {t('profile.remove-password.two-factor-stays')}
                </p>
              )}
              <p className="text-sm text-ink-500">
                {t('profile.remove-password.later')}
              </p>
            </>
          )}
          {errorLine}
          <Button
            variant="destructive"
            fullWidth
            disabled={busy}
            onClick={onConfirm}
          >
            {busy
              ? t('profile.remove-password.busy')
              : t('profile.remove-password.confirm')}
          </Button>
          <Button
            variant="secondary"
            fullWidth
            disabled={busy}
            onClick={onClose}
          >
            {t('common.cancel')}
          </Button>
        </div>
      )}
    </Sheet>
  )
}

export default RemovePasswordSheet
